import {
  type DocumentSearchMode,
  documentSearchModeSchema,
  type SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'
import { z } from 'zod'

import type { SearchEngine } from './documentSearchApiSchemas'

export const persistedDocumentSearchModeSchema = z
  .enum(['normal', 'ai', 'agentic'])
  .transform((value) => (value === 'agentic' ? 'ai' : value))
  .pipe(documentSearchModeSchema)

type PersistedDocChatState = NonNullable<
  SearchHistoryItem['searchState']['docChat']
>

function isPersistedDocChatState(
  value: unknown
): value is PersistedDocChatState {
  if (typeof value !== 'object' || value === null) return false
  if (!('siteId' in value) || typeof value.siteId !== 'string') return false
  if (
    !('conversation' in value) ||
    typeof value.conversation !== 'object' ||
    value.conversation === null
  ) {
    return false
  }
  return true
}

const persistedDocChatSchema = z.custom<PersistedDocChatState>(
  isPersistedDocChatState
)

export function parsePersistedDocumentSearchMode(
  value: unknown,
  fallback: DocumentSearchMode = 'ai'
): DocumentSearchMode {
  const parsed = persistedDocumentSearchModeSchema.safeParse(value)
  if (parsed.success) return parsed.data
  const parsedFallback = persistedDocumentSearchModeSchema.safeParse(fallback)
  return parsedFallback.success ? parsedFallback.data : 'ai'
}

export function migratePersistedSearchState(
  searchState: SearchHistoryItem['searchState']
): SearchHistoryItem['searchState'] {
  const searchMode = parsePersistedDocumentSearchMode(
    searchState.searchMode,
    'ai'
  )

  if (!('agenticSearch' in searchState)) {
    if (searchMode === searchState.searchMode) return searchState
    return { ...searchState, searchMode }
  }

  const { agenticSearch, ...rest } = searchState
  const legacyDocChat = persistedDocChatSchema.safeParse(agenticSearch)
  return {
    ...rest,
    searchMode,
    docChat:
      rest.docChat ?? (legacyDocChat.success ? legacyDocChat.data : undefined),
  }
}

export function isAiDocumentSearchEngine(
  engine: SearchEngine
): engine is Exclude<SearchEngine, 'unified'> {
  return engine === 'docChat'
}

export function getDocumentSearchEngine({
  searchMode,
  contentFilter,
  isLinkedDocsMode,
  browseAll,
}: {
  searchMode: DocumentSearchMode
  contentFilter: DocumentsViewerContentFilter
  isLinkedDocsMode: boolean
  browseAll: boolean
}): SearchEngine {
  if (
    searchMode === 'ai' &&
    contentFilter === 'documents' &&
    !isLinkedDocsMode &&
    !browseAll
  ) {
    return 'docChat'
  }
  return 'unified'
}
